import Box from "@memorang/ui/src/components/Box";
import TooltipWrapper from "@memorang/ui/src/components/TooltipWrapper";
import TextField from "@mui/material/TextField";
import { useEffect, useRef, useState } from "react";
import { Keyboard } from "react-native";
import { IconButton } from "react-native-paper";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import type { ToolActionProps } from "./ToolAction";
import ToolAction from "./ToolAction";

export type TextModeChatComposerProps = {
	onIconPress?: () => void;
	ToolActions?: ToolActionProps;
	textInputProps: { placeholder?: string; icon?: string };
	showSuggestions?: boolean;
	suggestions?: { text: string; onPress: () => void }[];
	disableSearch?: boolean;
	onAbortResponse: () => void;
	interceptResponseQuery: (query: string) => void;
	inputQuery: string;
	onChangeToVoiceMode?: () => void;
	STTMode?: boolean;
	isVoiceModeEnabled?: boolean;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Complex component with many conditional renderings
const TextModeChatComposer = (props: TextModeChatComposerProps) => {
	const inputRef = useRef<HTMLDivElement>(null);
	const theme = useAppTheme();
	const [isFocused, setIsFocused] = useState(false);

	const [localSearchQuery, setLocalSearchQuery] = useState(
		props.inputQuery || "",
	);
	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
		}
	}, []);
	const handleOnAction = () => {
		if (props.disableSearch) {
			props.onAbortResponse();
		} else {
			handleOnSubmit(localSearchQuery);
			setLocalSearchQuery("");
		}
	};

	const handleOnSubmit = (query: string) => {
		if (query.trim().length > 0) {
			Keyboard.dismiss();
			props.interceptResponseQuery(query.trim());
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		const { key: keyValue, shiftKey } = event;

		if (keyValue === "Enter" && !shiftKey) {
			event.preventDefault();
			handleOnAction();
		}
		if (keyValue === "Escape") {
			if (localSearchQuery.length > 0) {
				setLocalSearchQuery("");
			} else {
				inputRef?.current?.blur();
			}
		}
	};
	const shouldShowBorder =
		isFocused || localSearchQuery.length > 0 || props.showSuggestions;

	return (
		<Box>
			<Box
				backgroundColor={
					props.ToolActions ? theme.colors.surfaceContainerHigh : "transparent"
				}
				borderRadius={shouldShowBorder ? 28 : 100}
				borderTopLeftRadius={shouldShowBorder ? 28 : 100}
				borderTopRightRadius={shouldShowBorder ? 28 : 100}
			>
				{props.showSuggestions && (
					<Box paddingTop={16} paddingHorizontal={16} paddingBottom={12}>
						<Box gap={10}>
							{props.suggestions?.map((suggestion) => (
								<Box
									key={suggestion.text}
									// @ts-expect-error this is only intended for web, where it does get the proper press event
									onPress={suggestion.onPress}
									style={{ cursor: "pointer" }}
								>
									<Box>
										<Text
											variant="bodyMedium"
											style={{
												color: theme.colors.onSurfaceVariant,
												flexWrap: "wrap",
											}}
										>
											{suggestion.text}
										</Text>
									</Box>
								</Box>
							))}
						</Box>
					</Box>
				)}
				<Box>
					{props.ToolActions && (
						<Box paddingHorizontal={16} paddingTop={8}>
							<ToolAction
								key={props.ToolActions.text}
								icon={props.ToolActions.icon}
								text={props.ToolActions.text}
							/>
						</Box>
					)}
					<Box
						borderRadius={28}
						minHeight={56}
						maxHeight={96}
						alignItems="flex-end"
						paddingVertical={3}
						borderWidth={2}
						borderColor={
							shouldShowBorder
								? theme.colors.primary
								: theme.colors.onSurfaceVariant
						}
						flexDirection="row"
					>
						<TextField
							placeholder={props.textInputProps.placeholder}
							inputRef={inputRef}
							variant="outlined"
							disabled={props.disableSearch}
							value={localSearchQuery}
							size="medium"
							multiline
							maxRows={3}
							autoFocus
							onChange={(e) => setLocalSearchQuery(e.target.value)}
							onFocus={() => setIsFocused(true)}
							onBlur={() => setIsFocused(false)}
							sx={{
								flex: 1,
								"& .MuiOutlinedInput-root": {
									paddingBlock: 1.5,
									color: theme.colors.text,
									"& fieldset": {
										borderColor: "transparent",
									},
									"&:hover fieldset": {
										borderColor: "transparent",
									},
									"&.Mui-focused fieldset": {
										borderColor: "transparent",
									},
									"&.Mui-disabled fieldset": {
										borderColor: "transparent",
									},
								},
								"& .MuiOutlinedInput-input": {
									"&.Mui-disabled": {
										color: theme.colors.text,
										"-webkit-text-fill-color": theme.colors.text,
									},
									"&::-webkit-scrollbar": {
										display: "none",
									},
									// Firefox
									scrollbarWidth: "none",
									"-ms-overflow-style": "none",
								},
								"& .MuiInputAdornment-root.MuiInputAdornment-positionEnd.MuiInputAdornment-outlined.MuiInputAdornment-sizeMedium":
									{
										maxHeight: "unset !important",
										height: "100% !important",
										alignItems: "flex-end !important",
									},
							}}
							onKeyDown={handleKeyDown}
						/>
						<Box justifyContent="flex-end" height="100%">
							<TooltipWrapper
								title={
									localSearchQuery.length > 0
										? "Send"
										: props.disableSearch
											? "Stop"
											: props.isVoiceModeEnabled
												? "Voice mode"
												: "Send"
								}
								style={{
									padding: 4,
								}}
							>
								<IconButton
									mode="contained"
									disabled={
										props.disableSearch ? false : localSearchQuery.length === 0
									}
									containerColor={theme.colors.primary}
									size={24}
									style={{
										width: 40,
										height: 40,
										margin: 0,
										marginRight: 6,
									}}
									iconColor={theme.colors.onPrimary}
									icon={props.disableSearch ? "stop" : "arrow-up"}
									onPress={handleOnAction}
								/>
							</TooltipWrapper>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default TextModeChatComposer;
